// src/redux/slices/admin/transport-approve-slice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { transportReviewService } from "@/services/system-admin/transport-review.service";
import type {
  TransportApproveState,
  GetVehiclesParams,
  ApproveVehicleRequest,
  RequestChangesVehicleRequest,
  RejectVehicleRequest,
  SuspendVehicleRequest,
  RestoreVehicleRequest,
  VerifyDocumentRequest,
  RejectDocumentRequest,
} from "@/types/admin/transport-approve-type";

const initialState: TransportApproveState = {
  vehicles: [],
  availability: [],
  availabilityLoading: false,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,

  statusCounts: {},
  countsLoading: false,

  vehicleDetail: null,
  detailLoading: false,

  reviews: [],
  reviewsLoading: false,

  actionLoading: false,
};

export const fetchVehicles = createAsyncThunk(
  "transportApprove/fetchVehicles",
  async (params: GetVehiclesParams) => {
    const response = await transportReviewService.getVehicles(params);
    return {
      ...response,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
  },
);

/**
 * Fetches every vehicle (unfiltered, large page size) purely to compute a
 * count per status for the filter chips. If your vehicle count grows large,
 * swap this for a dedicated backend /counts endpoint instead.
 */
export const fetchVehicleStatusCounts = createAsyncThunk(
  "transportApprove/fetchVehicleStatusCounts",
  async () => {
    const response = await transportReviewService.getVehicles({
      page: 1,
      pageSize: 1000,
    });
    const counts: Record<string, number> = {};
    for (const item of response.items) {
      counts[item.status] = (counts[item.status] || 0) + 1;
    }
    counts.ALL = response.totalCount;
    return counts;
  },
);

export const fetchVehicleDetail = createAsyncThunk(
  "transportApprove/fetchVehicleDetail",
  async (vehicleUid: string) => transportReviewService.getVehicleById(vehicleUid),
);

export const fetchVehicleReviews = createAsyncThunk(
  "transportApprove/fetchVehicleReviews",
  async (vehicleUid: string) =>
    transportReviewService.getVehicleReviews(vehicleUid),
);

export const fetchVehicleAvailability = createAsyncThunk(
  "transportApprove/fetchVehicleAvailability",
  async (vehicleUid: string) =>
    transportReviewService.getVehicleAvailability(vehicleUid),
);

export const approveVehicle = createAsyncThunk(
  "transportApprove/approveVehicle",
  async ({
    vehicleUid,
    payload,
  }: {
    vehicleUid: string;
    payload: ApproveVehicleRequest;
  }) => {
    await transportReviewService.approveVehicle(vehicleUid, payload);
    return transportReviewService.getVehicleById(vehicleUid);
  },
);

export const requestVehicleChanges = createAsyncThunk(
  "transportApprove/requestVehicleChanges",
  async ({
    vehicleUid,
    payload,
  }: {
    vehicleUid: string;
    payload: RequestChangesVehicleRequest;
  }) => {
    await transportReviewService.requestChanges(vehicleUid, payload);
    return transportReviewService.getVehicleById(vehicleUid);
  },
);

export const rejectVehicle = createAsyncThunk(
  "transportApprove/rejectVehicle",
  async ({
    vehicleUid,
    payload,
  }: {
    vehicleUid: string;
    payload: RejectVehicleRequest;
  }) => {
    await transportReviewService.rejectVehicle(vehicleUid, payload);
    return transportReviewService.getVehicleById(vehicleUid);
  },
);

export const suspendVehicle = createAsyncThunk(
  "transportApprove/suspendVehicle",
  async ({
    vehicleUid,
    payload,
  }: {
    vehicleUid: string;
    payload: SuspendVehicleRequest;
  }) => {
    await transportReviewService.suspendVehicle(vehicleUid, payload);
    return transportReviewService.getVehicleById(vehicleUid);
  },
);

export const restoreVehicle = createAsyncThunk(
  "transportApprove/restoreVehicle",
  async ({
    vehicleUid,
    payload,
  }: {
    vehicleUid: string;
    payload: RestoreVehicleRequest;
  }) => {
    await transportReviewService.restoreVehicle(vehicleUid, payload);
    return transportReviewService.getVehicleById(vehicleUid);
  },
);

export const verifyVehicleDocument = createAsyncThunk(
  "transportApprove/verifyVehicleDocument",
  async ({
    documentUid,
    payload,
  }: {
    documentUid: string;
    payload: VerifyDocumentRequest;
  }) => {
    await transportReviewService.verifyDocument(documentUid, payload);
    return documentUid;
  },
);

export const rejectVehicleDocument = createAsyncThunk(
  "transportApprove/rejectVehicleDocument",
  async ({
    documentUid,
    payload,
  }: {
    documentUid: string;
    payload: RejectDocumentRequest;
  }) => {
    await transportReviewService.rejectDocument(documentUid, payload);
    return documentUid;
  },
);

const transportApproveSlice = createSlice({
  name: "transportApprove",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearVehicleDetail: (state) => {
      state.vehicleDetail = null;
      state.reviews = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.vehicles = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
     .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch vehicles.";
      })

      // ---- status counts ----
      .addCase(fetchVehicleStatusCounts.pending, (state) => {
        state.countsLoading = true;
      })
      .addCase(fetchVehicleStatusCounts.fulfilled, (state, action) => {
        state.countsLoading = false;
        state.statusCounts = action.payload;
      })
      .addCase(fetchVehicleStatusCounts.rejected, (state) => {
        state.countsLoading = false;
      })

      // ---- detail ----
      .addCase(fetchVehicleDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicleDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(fetchVehicleDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || "Failed to fetch vehicle.";
      })

      // ---- reviews ----
      .addCase(fetchVehicleReviews.pending, (state) => {
        state.reviewsLoading = true;
      })
      .addCase(fetchVehicleReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload;
      })
         .addCase(fetchVehicleReviews.rejected, (state) => {
        state.reviewsLoading = false;
      })

      // ---- availability ----
      .addCase(fetchVehicleAvailability.pending, (state) => {
        state.availabilityLoading = true;
      })
      .addCase(fetchVehicleAvailability.fulfilled, (state, action) => {
        state.availabilityLoading = false;
        state.availability = action.payload;
      })
      .addCase(fetchVehicleAvailability.rejected, (state) => {
        state.availabilityLoading = false;
      })

      // ---- approve ----
      .addCase(approveVehicle.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(approveVehicle.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(approveVehicle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to approve vehicle.";
      })

      // ---- request changes ----
      .addCase(requestVehicleChanges.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(requestVehicleChanges.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(requestVehicleChanges.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to request changes.";
      })

      // ---- reject ----
      .addCase(rejectVehicle.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectVehicle.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(rejectVehicle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to reject vehicle.";
      })

      // ---- suspend ----
      .addCase(suspendVehicle.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(suspendVehicle.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(suspendVehicle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to suspend vehicle.";
      })

      // ---- restore ----
      .addCase(restoreVehicle.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(restoreVehicle.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.vehicleDetail = action.payload;
      })
      .addCase(restoreVehicle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to restore vehicle.";
      })

      // ---- document verify ----
      .addCase(verifyVehicleDocument.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(verifyVehicleDocument.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (state.vehicleDetail) {
          state.vehicleDetail.documents = state.vehicleDetail.documents.map(
            (d) => (d.uid === action.payload ? { ...d, status: "VERIFIED" } : d),
          );
        }
      })
      .addCase(verifyVehicleDocument.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to verify document.";
      })

      // ---- document reject ----
      .addCase(rejectVehicleDocument.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectVehicleDocument.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (state.vehicleDetail) {
          state.vehicleDetail.documents = state.vehicleDetail.documents.map(
            (d) => (d.uid === action.payload ? { ...d, status: "REJECTED" } : d),
          );
        }
      })
      .addCase(rejectVehicleDocument.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to reject document.";
      });
  },
});

export const { setPage, setPageSize, clearVehicleDetail, clearError } =
  transportApproveSlice.actions;
export default transportApproveSlice.reducer;